export type OpenOrderSizePlan = {
  rawNotional: number
  requestedSize: string
  quantizedNotional: number
}

export function buildOpenOrderSizePlan({
  margin,
  leverage,
  entryPrice,
  szDecimals,
}: {
  margin: number
  leverage: number
  entryPrice: number
  szDecimals: number
}): OpenOrderSizePlan {
  const rawNotional = margin * leverage
  const requestedSize = formatHLSize(
    (rawNotional / entryPrice).toString(),
    szDecimals
  )
  const quantizedNotional = Number(requestedSize) * entryPrice

  return {
    rawNotional,
    requestedSize,
    quantizedNotional,
  }
}

export function formatHLSize(size: string, szDecimals: number): string {
  const decimal = normalizePositiveDecimal(size)
  const [whole, fraction = ""] = decimal.split(".")
  const truncatedFraction = fraction.slice(0, szDecimals).replace(/0+$/, "")
  const normalizedWhole = whole.replace(/^0+(?=\d)/, "") || "0"
  const formatted =
    truncatedFraction.length > 0
      ? `${normalizedWhole}.${truncatedFraction}`
      : normalizedWhole
  const numeric = Number(formatted)
  if (!Number.isFinite(numeric)) {
    throw new Error("HL size must be a finite decimal")
  }
  if (numeric <= 0) {
    throw new Error("HL size is too small for this asset precision")
  }
  return formatted
}

function normalizePositiveDecimal(value: string): string {
  const trimmed = value.trim()
  if (trimmed.startsWith("-") || trimmed.startsWith("+")) {
    throw new Error("HL size must be an unsigned decimal")
  }
  const expanded = expandExponentialDecimal(trimmed)
  if (!/^\d+(\.\d+)?$/.test(expanded)) {
    throw new Error("HL size must be a finite decimal")
  }
  return expanded
}

function expandExponentialDecimal(value: string): string {
  if (!/[eE]/.test(value)) {
    return value
  }

  const [coefficient, exponentRaw, extra] = value.toLowerCase().split("e")
  const exponent = Number(exponentRaw)
  if (
    coefficient == null ||
    exponentRaw == null ||
    extra != null ||
    !/^\d+(\.\d+)?$/.test(coefficient) ||
    !/^[+-]?\d+$/.test(exponentRaw) ||
    !Number.isSafeInteger(exponent)
  ) {
    throw new Error("HL size must be a finite decimal")
  }

  const [whole, fraction = ""] = coefficient.split(".")
  const digits = `${whole}${fraction}`
  const decimalIndex = whole.length + exponent
  if (decimalIndex <= 0) {
    return `0.${"0".repeat(Math.abs(decimalIndex))}${digits}`
  }
  if (decimalIndex >= digits.length) {
    return `${digits}${"0".repeat(decimalIndex - digits.length)}`
  }
  return `${digits.slice(0, decimalIndex)}.${digits.slice(decimalIndex)}`
}
